/**
 * Sistema de Criptografia Pós-Quântica (PQC) Simulado
 * 
 * Este módulo simula algoritmos de criptografia resistentes a ataques quânticos:
 * - Kyber: Para troca de chaves (Key Encapsulation Mechanism)
 * - Dilithium: Para assinaturas digitais
 * 
 * NOTA: Esta é uma implementação simulada para demonstração.
 * Em produção, seria necessário usar bibliotecas reais de PQC.
 */

import { toast } from 'sonner'

// Tipos para chaves PQC
interface KyberKeyPair {
  publicKey: string
  privateKey: string
  algorithm: 'Kyber-512' | 'Kyber-768' | 'Kyber-1024'
  createdAt: string
}

interface DilithiumKeyPair {
  publicKey: string
  privateKey: string
  algorithm: 'Dilithium2' | 'Dilithium3' | 'Dilithium5'
  createdAt: string
}

interface EncryptedData {
  ciphertext: string
  encapsulatedKey: string
  algorithm: string
  timestamp: string
  signature?: string
}

interface SignedData {
  data: string
  signature: string
  algorithm: string
  timestamp: string
  publicKey: string
}

// Simulação de parâmetros de segurança
const SECURITY_LEVELS = {
  'Kyber-512': { keySize: 800, ciphertextSize: 768, sharedSecretSize: 32 },
  'Kyber-768': { keySize: 1184, ciphertextSize: 1088, sharedSecretSize: 32 },
  'Kyber-1024': { keySize: 1568, ciphertextSize: 1568, sharedSecretSize: 32 },
  'Dilithium2': { publicKeySize: 1312, privateKeySize: 2528, signatureSize: 2420 },
  'Dilithium3': { publicKeySize: 1952, privateKeySize: 4000, signatureSize: 3293 },
  'Dilithium5': { publicKeySize: 2592, privateKeySize: 4864, signatureSize: 4595 }
}

class PQCCrypto {
  private kyberKeys: Map<string, KyberKeyPair> = new Map()
  private dilithiumKeys: Map<string, DilithiumKeyPair> = new Map()
  private isInitialized = false

  constructor() {
    this.initialize()
  }

  private async initialize() {
    try {
      // Simular inicialização de bibliotecas PQC
      await new Promise(resolve => setTimeout(resolve, 100))
      this.isInitialized = true
      console.log('Sistema PQC inicializado com sucesso')
    } catch (error) {
      console.error('Erro ao inicializar PQC:', error)
    }
  }

  /**
   * Gerar par de chaves Kyber para troca de chaves
   */
  async generateKyberKeyPair(
    userId: string,
    algorithm: 'Kyber-512' | 'Kyber-768' | 'Kyber-1024' = 'Kyber-768'
  ): Promise<KyberKeyPair> {
    try {
      if (!this.isInitialized) {
        await this.initialize()
      }

      const params = SECURITY_LEVELS[algorithm]
      
      // Simular geração de chaves
      const publicKey = this.generateRandomKey(params.keySize)
      const privateKey = this.generateRandomKey(params.keySize)
      
      const keyPair: KyberKeyPair = {
        publicKey,
        privateKey,
        algorithm,
        createdAt: new Date().toISOString()
      }

      // Armazenar chave localmente
      this.kyberKeys.set(userId, keyPair)
      
      console.log(`Chaves Kyber ${algorithm} geradas para usuário ${userId}`)
      return keyPair
    } catch (error) {
      console.error('Erro ao gerar chaves Kyber:', error)
      throw new Error('Falha na geração de chaves Kyber')
    }
  }

  /**
   * Gerar par de chaves Dilithium para assinaturas
   */
  async generateDilithiumKeyPair(
    userId: string,
    algorithm: 'Dilithium2' | 'Dilithium3' | 'Dilithium5' = 'Dilithium3'
  ): Promise<DilithiumKeyPair> {
    try {
      if (!this.isInitialized) {
        await this.initialize()
      }

      const params = SECURITY_LEVELS[algorithm]
      
      // Simular geração de chaves
      const publicKey = this.generateRandomKey(params.publicKeySize)
      const privateKey = this.generateRandomKey(params.privateKeySize)
      
      const keyPair: DilithiumKeyPair = {
        publicKey,
        privateKey,
        algorithm,
        createdAt: new Date().toISOString()
      }

      // Armazenar chave localmente
      this.dilithiumKeys.set(userId, keyPair)
      
      console.log(`Chaves Dilithium ${algorithm} geradas para usuário ${userId}`)
      return keyPair
    } catch (error) {
      console.error('Erro ao gerar chaves Dilithium:', error)
      throw new Error('Falha na geração de chaves Dilithium')
    }
  }

  /**
   * Criptografar dados usando Kyber
   */
  async encryptData(
    data: string,
    recipientPublicKey: string,
    algorithm: 'Kyber-512' | 'Kyber-768' | 'Kyber-1024' = 'Kyber-768'
  ): Promise<EncryptedData> {
    try {
      console.log('🔐 [PQC] Iniciando criptografia de dados:', {
        algorithm,
        dataLength: data.length,
        recipientPublicKeyLength: recipientPublicKey?.length || 0
      })
      
      if (!this.isInitialized) {
        console.log('🔧 [PQC] Sistema não inicializado, inicializando...')
        await this.initialize()
      }

      // Validar dados de entrada
      if (!data) {
        throw new Error('Dados para criptografia não fornecidos')
      }
      
      if (!recipientPublicKey) {
        throw new Error('Chave pública do destinatário não fornecida')
      }

      const params = SECURITY_LEVELS[algorithm]
      
      console.log('🔑 [PQC] Simulando encapsulamento de chave...')
      // Simular encapsulamento de chave determinístico
      const encapsulatedKey = this.simulateKeyEncapsulation(recipientPublicKey, params.ciphertextSize)
      const sharedSecret = this.simulateKeyDecapsulation(encapsulatedKey, recipientPublicKey)
      
      console.log('🔍 [PQC] Chave compartilhada gerada para criptografia:', {
        sharedSecretLength: sharedSecret.length,
        sharedSecretPrefix: sharedSecret.substring(0, 8) + '...'
      })
      
      console.log('🔐 [PQC] Iniciando criptografia simétrica...')
      // Simular criptografia simétrica com a chave compartilhada
      const ciphertext = this.symmetricEncrypt(data, sharedSecret)
      
      const encryptedData: EncryptedData = {
        ciphertext,
        encapsulatedKey,
        algorithm,
        timestamp: new Date().toISOString()
      }

      console.log(`✅ [PQC] Dados criptografados com sucesso usando ${algorithm}`, {
        ciphertextLength: ciphertext.length,
        encapsulatedKeyLength: encapsulatedKey.length
      })
      return encryptedData
    } catch (error) {
      console.error('❌ [PQC] Erro ao criptografar dados:', error)
      if (error instanceof Error) {
        throw new Error(`Falha na criptografia: ${error.message}`)
      }
      throw new Error('Falha na criptografia')
    }
  }

  /**
   * Descriptografar dados usando Kyber
   */
  async decryptData(
    encryptedData: EncryptedData,
    privateKey: string
  ): Promise<string> {
    try {
      console.log('🔓 [PQC] Iniciando descriptografia de dados:', {
        algorithm: encryptedData.algorithm,
        ciphertextLength: encryptedData.ciphertext?.length || 0,
        encapsulatedKeyLength: encryptedData.encapsulatedKey?.length || 0,
        privateKeyLength: privateKey?.length || 0,
        timestamp: encryptedData.timestamp
      })
      
      if (!this.isInitialized) {
        console.log('🔧 [PQC] Sistema não inicializado, inicializando...')
        await this.initialize()
      }
      
      // Validar dados de entrada
      if (!encryptedData || !encryptedData.ciphertext || !encryptedData.encapsulatedKey) {
        throw new Error('Dados criptografados inválidos ou incompletos')
      }
      
      if (!privateKey) {
        throw new Error('Chave privada não fornecida')
      }

      console.log('🔑 [PQC] Simulando desencapsulamento de chave...')
      // Simular desencapsulamento de chave
      const sharedSecret = this.simulateKeyDecapsulation(
        encryptedData.encapsulatedKey,
        privateKey
      )
      
      console.log('🔍 [PQC] Chave compartilhada gerada:', {
        sharedSecretLength: sharedSecret.length,
        sharedSecretPrefix: sharedSecret.substring(0, 8) + '...'
      })
      
      console.log('🔓 [PQC] Iniciando descriptografia simétrica...')
      // Simular descriptografia simétrica
      const decryptedData = this.symmetricDecrypt(
        encryptedData.ciphertext,
        sharedSecret
      )
      
      console.log(`✅ [PQC] Dados descriptografados com sucesso usando ${encryptedData.algorithm}`, {
        decryptedDataLength: decryptedData.length
      })
      return decryptedData
    } catch (error) {
      console.error('❌ [PQC] Erro ao descriptografar dados:', error)
      if (error instanceof Error) {
        throw new Error(`Falha na descriptografia: ${error.message}`)
      }
      throw new Error('Falha na descriptografia')
    }
  }

  /**
   * Assinar dados usando Dilithium
   */
  async signData(
    data: string,
    privateKey: string,
    algorithm: 'Dilithium2' | 'Dilithium3' | 'Dilithium5' = 'Dilithium3'
  ): Promise<SignedData> {
    try {
      if (!this.isInitialized) {
        await this.initialize()
      }

      const params = SECURITY_LEVELS[algorithm]
      
      // Simular assinatura digital
      const signature = this.generateSignature(data, privateKey, params.signatureSize)
      
      // Obter chave pública correspondente
      const publicKey = this.getPublicKeyFromPrivate(privateKey)
      
      const signedData: SignedData = {
        data,
        signature,
        algorithm,
        timestamp: new Date().toISOString(),
        publicKey
      }

      console.log(`Dados assinados com ${algorithm}`)
      return signedData
    } catch (error) {
      console.error('Erro ao assinar dados:', error)
      throw new Error('Falha na assinatura')
    }
  }

  /**
   * Verificar assinatura usando Dilithium
   */
  async verifySignature(
    signedData: SignedData
  ): Promise<boolean> {
    try {
      if (!this.isInitialized) {
        await this.initialize()
      }

      // Simular verificação de assinatura
      const isValid = this.simulateSignatureVerification(
        signedData.data,
        signedData.signature,
        signedData.publicKey
      )
      
      console.log(`Assinatura ${isValid ? 'válida' : 'inválida'} com ${signedData.algorithm}`)
      return isValid
    } catch (error) {
      console.error('Erro ao verificar assinatura:', error)
      return false
    }
  }

  /**
   * Criptografar e assinar dados (operação combinada)
   */
  async encryptAndSign(
    data: string,
    recipientPublicKey: string,
    senderPrivateKey: string,
    kyberAlgorithm: 'Kyber-512' | 'Kyber-768' | 'Kyber-1024' = 'Kyber-768',
    dilithiumAlgorithm: 'Dilithium2' | 'Dilithium3' | 'Dilithium5' = 'Dilithium3'
  ): Promise<EncryptedData> {
    try {
      // Primeiro, assinar os dados
      const signedData = await this.signData(data, senderPrivateKey, dilithiumAlgorithm)
      
      // Depois, criptografar os dados assinados
      const encryptedData = await this.encryptData(
        JSON.stringify(signedData),
        recipientPublicKey,
        kyberAlgorithm
      )
      
      // Adicionar informação de assinatura
      encryptedData.signature = signedData.signature
      
      console.log('Dados criptografados e assinados com sucesso')
      return encryptedData
    } catch (error) {
      console.error('Erro ao criptografar e assinar:', error)
      throw new Error('Falha na operação combinada')
    }
  }

  /**
   * Descriptografar e verificar assinatura
   */
  async decryptAndVerify(
    encryptedData: EncryptedData,
    recipientPrivateKey: string
  ): Promise<{ data: string; isSignatureValid: boolean }> {
    try {
      console.log('🔓🔍 [PQC] Iniciando descriptografia e verificação:', {
        algorithm: encryptedData.algorithm,
        hasSignature: !!encryptedData.signature,
        privateKeyLength: recipientPrivateKey?.length || 0
      })
      
      // Validar dados de entrada
      if (!encryptedData) {
        throw new Error('Dados criptografados não fornecidos')
      }
      
      if (!recipientPrivateKey) {
        throw new Error('Chave privada do destinatário não fornecida')
      }
      
      console.log('🔓 [PQC] Etapa 1: Descriptografando dados...')
      // Primeiro, descriptografar
      const decryptedJson = await this.decryptData(encryptedData, recipientPrivateKey)
      
      console.log('📝 [PQC] Etapa 2: Parseando dados assinados...', {
        decryptedJsonLength: decryptedJson.length
      })
      
      let signedData: SignedData
      try {
        signedData = JSON.parse(decryptedJson)
      } catch (parseError) {
        console.error('❌ [PQC] Erro ao parsear dados assinados:', parseError)
        throw new Error('Formato de dados assinados inválido')
      }
      
      console.log('🔍 [PQC] Dados assinados parseados:', {
        hasData: !!signedData.data,
        hasSignature: !!signedData.signature,
        hasPublicKey: !!signedData.publicKey,
        algorithm: signedData.algorithm,
        timestamp: signedData.timestamp
      })
      
      console.log('🔍 [PQC] Etapa 3: Verificando assinatura...')
      // Depois, verificar assinatura
      const isSignatureValid = await this.verifySignature(signedData)
      
      console.log('✅ [PQC] Processo completo finalizado:', {
        dataLength: signedData.data.length,
        isSignatureValid
      })
      
      return {
        data: signedData.data,
        isSignatureValid
      }
    } catch (error) {
      console.error('❌ [PQC] Erro ao descriptografar e verificar:', error)
      if (error instanceof Error) {
        throw new Error(`Falha na verificação: ${error.message}`)
      }
      throw new Error('Falha na verificação')
    }
  }

  /**
   * Obter chaves do usuário
   */
  getUserKeys(userId: string): {
    kyber: KyberKeyPair | null
    dilithium: DilithiumKeyPair | null
  } {
    return {
      kyber: this.kyberKeys.get(userId) || null,
      dilithium: this.dilithiumKeys.get(userId) || null
    }
  }

  /**
   * Definir chaves do usuário (para carregar do banco)
   */
  setUserKeys(userId: string, kyberKeys: KyberKeyPair, dilithiumKeys: DilithiumKeyPair): void {
    this.kyberKeys.set(userId, kyberKeys)
    this.dilithiumKeys.set(userId, dilithiumKeys)
    console.log(`Chaves carregadas no cache para usuário ${userId}`)
  }

  /**
   * Verificar se usuário tem chaves
   */
  hasKeys(userId: string): boolean {
    return this.kyberKeys.has(userId) && this.dilithiumKeys.has(userId)
  }

  /**
   * Remover chaves do usuário
   */
  removeUserKeys(userId: string): void {
    this.kyberKeys.delete(userId)
    this.dilithiumKeys.delete(userId)
    console.log(`Chaves removidas para usuário ${userId}`)
  }

  // Métodos auxiliares para simulação
  private generateRandomKey(size: number): string {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/'
    let result = ''
    for (let i = 0; i < size; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length))
    }
    return btoa(result).substring(0, size)
  }

  private symmetricEncrypt(data: string, key: string): string {
    // Simulação de criptografia simétrica (AES-256)
    const encrypted = btoa(data + '::' + key.substring(0, 16))
    return encrypted
  }

  private symmetricDecrypt(ciphertext: string, key: string): string {
    // Simulação de descriptografia simétrica mais robusta
    try {
      console.log('🔓 [PQC] Iniciando descriptografia simétrica:', {
        ciphertextLength: ciphertext.length,
        keyLength: key.length,
        keyPrefix: key.substring(0, 8) + '...'
      })
      
      if (!ciphertext || !key) {
        throw new Error('Dados de entrada inválidos')
      }
      
      // Tentar decodificar base64
      let decoded: string
      try {
        decoded = atob(ciphertext)
      } catch (error) {
        console.error('❌ [PQC] Erro ao decodificar base64:', error)
        throw new Error('Formato de dados criptografados inválido')
      }
      
      console.log('🔍 [PQC] Dados decodificados:', {
        decodedLength: decoded.length,
        containsSeparator: decoded.includes('::')
      })
      
      const parts = decoded.split('::')
      if (parts.length !== 2) {
        console.error('❌ [PQC] Formato inválido - esperado 2 partes, encontrado:', parts.length)
        throw new Error('Formato de dados criptografados inválido')
      }
      
      const [data, embeddedKey] = parts
      const expectedKey = key.substring(0, 16)
      
      console.log('🔍 [PQC] Verificando chaves:', {
        embeddedKeyLength: embeddedKey.length,
        expectedKeyLength: expectedKey.length,
        keysMatch: embeddedKey === expectedKey
      })
      
      if (embeddedKey !== expectedKey) {
        console.error('❌ [PQC] Chave não corresponde:', {
          embedded: embeddedKey,
          expected: expectedKey
        })
        throw new Error('Chave de descriptografia inválida')
      }
      
      console.log('✅ [PQC] Descriptografia simétrica bem-sucedida')
      return data
    } catch (error) {
      console.error('❌ [PQC] Erro na descriptografia simétrica:', error)
      if (error instanceof Error) {
        throw error
      }
      throw new Error('Falha na descriptografia')
    }
  }

  private simulateKeyEncapsulation(publicKey: string, size: number): string {
    // Simulação de encapsulamento de chave determinístico
    try {
      console.log('🔑 [PQC] Simulando encapsulamento de chave:', {
        publicKeyLength: publicKey?.length || 0,
        targetSize: size
      })
      
      if (!publicKey) {
        throw new Error('Chave pública não fornecida')
      }
      
      if (publicKey.length < 10) {
        throw new Error('Chave pública muito curta para ser válida')
      }
      
      // Gerar chave encapsulada determinística baseada na chave pública
      const hash = this.simpleHash('encapsulate_' + publicKey)
      const encapsulatedKey = hash.padEnd(size, '0').substring(0, size)
      
      console.log('✅ [PQC] Chave encapsulada gerada com sucesso:', {
        encapsulatedKeyLength: encapsulatedKey.length
      })
      
      return encapsulatedKey
    } catch (error) {
      console.error('❌ [PQC] Erro no encapsulamento de chave:', error)
      throw error
    }
  }

  private simulateKeyDecapsulation(encapsulatedKey: string, privateKey: string): string {
    // Simulação de desencapsulamento de chave com validação
    try {
      console.log('🔑 [PQC] Simulando desencapsulamento de chave:', {
        encapsulatedKeyLength: encapsulatedKey?.length || 0,
        privateKeyLength: privateKey?.length || 0
      })
      
      if (!encapsulatedKey || !privateKey) {
        throw new Error('Chave encapsulada ou chave privada não fornecida')
      }
      
      if (encapsulatedKey.length < 10 || privateKey.length < 10) {
        throw new Error('Chaves muito curtas para serem válidas')
      }
      
      // Para compatibilidade: se a chave privada for igual à pública (auto-criptografia)
      // usar a mesma lógica do encapsulamento
      const hash = this.simpleHash('encapsulate_' + privateKey)
      const expectedEncapsulatedKey = hash.padEnd(encapsulatedKey.length, '0').substring(0, encapsulatedKey.length)
      
      // Verificar se a chave encapsulada corresponde ao esperado
      if (encapsulatedKey === expectedEncapsulatedKey) {
        // Gerar chave compartilhada determinística
        const sharedSecretHash = this.simpleHash('shared_' + privateKey + '_' + encapsulatedKey)
        const sharedSecret = sharedSecretHash.substring(0, 32)
        
        console.log('✅ [PQC] Chave compartilhada gerada com sucesso:', {
          sharedSecretLength: sharedSecret.length,
          sharedSecretPrefix: sharedSecret.substring(0, 8) + '...'
        })
        
        return sharedSecret
      } else {
        // Fallback para compatibilidade com chaves diferentes
        const fallbackHash = this.simpleHash(encapsulatedKey + privateKey)
        const sharedSecret = fallbackHash.substring(0, 32)
        
        console.log('⚠️ [PQC] Usando fallback para chave compartilhada:', {
          sharedSecretLength: sharedSecret.length
        })
        
        return sharedSecret
      }
    } catch (error) {
      console.error('❌ [PQC] Erro no desencapsulamento de chave:', error)
      throw error
    }
  }

  private generateSignature(data: string, privateKey: string, size: number): string {
    // Simulação de assinatura digital
    console.log('✍️ [PQC] Gerando assinatura:', {
      dataLength: data.length,
      privateKeyLength: privateKey.length,
      dataPreview: data.substring(0, 20) + '...',
      privateKeyPreview: privateKey.substring(0, 16) + '...'
    })
    
    // Para auto-assinatura, vamos usar uma abordagem que seja compatível
    // com a verificação. Como getPublicKeyFromPrivate(privateKey) gera a chave pública,
    // vamos usar a chave pública correspondente na assinatura para garantir compatibilidade
    const correspondingPublicKey = this.getPublicKeyFromPrivate(privateKey)
    
    // Gerar assinatura usando a chave pública correspondente
    // (isso garante que a verificação funcione)
    const hash = this.simpleHash(data + correspondingPublicKey)
    const signature = hash.padEnd(size, '0')
    
    console.log('✅ [PQC] Assinatura gerada:', {
      correspondingPublicKey: correspondingPublicKey.substring(0, 16) + '...',
      signaturePreview: signature.substring(0, 16) + '...',
      signatureLength: signature.length
    })
    
    return signature
  }

  private getPublicKeyFromPrivate(privateKey: string): string {
    // Simulação de derivação de chave pública
    // Usar hash consistente para manter compatibilidade
    return this.simpleHash('public_' + privateKey)
  }

  private getPrivateKeyFromPublic(publicKey: string): string {
    // CORREÇÃO: Para auto-assinatura, quando usamos nossa própria chave,
    // a chave privada e pública são do mesmo par de chaves
    // Vamos usar uma abordagem que funcione para auto-assinatura
    
    // Para auto-assinatura: se publicKey foi gerada de privateKey,
    // então devemos retornar o privateKey original
    // Como não podemos "reverter" o hash, vamos usar uma abordagem diferente:
    // assumir que para auto-assinatura, a chave privada é a própria chave pública
    // (simulação simplificada para demonstração)
    return publicKey
  }

  private simulateSignatureVerification(
    data: string,
    signature: string,
    publicKey: string
  ): boolean {
    try {
      console.log('🔍 [PQC] Verificando assinatura:', {
        dataLength: data.length,
        signatureLength: signature.length,
        publicKeyLength: publicKey.length,
        dataPreview: data.substring(0, 20) + '...',
        signaturePreview: signature.substring(0, 16) + '...',
        publicKeyPreview: publicKey.substring(0, 16) + '...'
      })
      
      // CORREÇÃO DEFINITIVA: Para auto-assinatura, vamos usar uma lógica mais simples e consistente
      // Quando o usuário assina com sua própria chave privada e verifica com sua chave pública,
      // ambas as chaves são do mesmo par
      
      // Abordagem 1: Verificação direta usando a chave pública
      // (para auto-assinatura, a chave pública pode ser usada diretamente)
      const directSignature = this.simpleHash(data + publicKey)
      const directMatch = signature.substring(0, 32) === directSignature.substring(0, 32)
      
      console.log('🔍 [PQC] Verificação direta (auto-assinatura):', {
        expectedSignature: directSignature.substring(0, 32),
        receivedSignature: signature.substring(0, 32),
        directMatch
      })
      
      if (directMatch) {
        console.log('✅ [PQC] Assinatura válida (verificação direta)')
        return true
      }
      
      // Abordagem 2: Verificação usando chave privada derivada
      const derivedPrivateKey = this.getPrivateKeyFromPublic(publicKey)
      const derivedSignature = this.simpleHash(data + derivedPrivateKey)
      const derivedMatch = signature.substring(0, 32) === derivedSignature.substring(0, 32)
      
      console.log('🔍 [PQC] Verificação com chave derivada:', {
        derivedPrivateKey: derivedPrivateKey.substring(0, 16) + '...',
        expectedSignature: derivedSignature.substring(0, 32),
        derivedMatch
      })
      
      if (derivedMatch) {
        console.log('✅ [PQC] Assinatura válida (verificação derivada)')
        return true
      }
      
      // Abordagem 3: Verificação reversa - tentar diferentes combinações
      // para garantir compatibilidade com diferentes cenários
      const reverseSignature = this.simpleHash(publicKey + data)
      const reverseMatch = signature.substring(0, 32) === reverseSignature.substring(0, 32)
      
      console.log('🔍 [PQC] Verificação reversa:', {
        expectedSignature: reverseSignature.substring(0, 32),
        reverseMatch
      })
      
      if (reverseMatch) {
        console.log('✅ [PQC] Assinatura válida (verificação reversa)')
        return true
      }
      
      console.log('❌ [PQC] Todas as verificações falharam - assinatura inválida')
      return false
    } catch (error) {
      console.error('❌ [PQC] Erro na verificação de assinatura:', error)
      return false
    }
  }

  private simpleHash(input: string): string {
    // Simulação de função hash (não usar em produção)
    let hash = 0
    for (let i = 0; i < input.length; i++) {
      const char = input.charCodeAt(i)
      hash = ((hash << 5) - hash) + char
      hash = hash & hash // Converter para 32bit
    }
    return Math.abs(hash).toString(16).padStart(32, '0')
  }

  /**
   * Obter informações sobre algoritmos suportados
   */
  getSupportedAlgorithms(): {
    kyber: string[]
    dilithium: string[]
    securityLevels: typeof SECURITY_LEVELS
  } {
    return {
      kyber: ['Kyber-512', 'Kyber-768', 'Kyber-1024'],
      dilithium: ['Dilithium2', 'Dilithium3', 'Dilithium5'],
      securityLevels: SECURITY_LEVELS
    }
  }

  /**
   * Verificar status do sistema PQC
   */
  getStatus(): {
    isInitialized: boolean
    totalKyberKeys: number
    totalDilithiumKeys: number
    supportedAlgorithms: string[]
  } {
    return {
      isInitialized: this.isInitialized,
      totalKyberKeys: this.kyberKeys.size,
      totalDilithiumKeys: this.dilithiumKeys.size,
      supportedAlgorithms: [
        'Kyber-512', 'Kyber-768', 'Kyber-1024',
        'Dilithium2', 'Dilithium3', 'Dilithium5'
      ]
    }
  }
}

// Instância singleton
const pqcCrypto = new PQCCrypto()

export default pqcCrypto
export type {
  KyberKeyPair,
  DilithiumKeyPair,
  EncryptedData,
  SignedData
}